/**
 * Product Media Zoom — click to zoom, click again to reset, mouse-move to pan.
 * Uses event delegation so Swiper loop clones and variant gallery swaps keep working.
 */

import { Component } from './component-base.js';

const ZOOMABLE_SELECTOR = '[data-zoomable]';

/**
 * Custom element for product image zoom on click
 * @extends Component
 */
export class ProductMediaZoom extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.zoomScale = this.dataset.zoomScale || '2';
    this.transitionDuration = this.dataset.transitionDuration || '400ms';
    this.zoomedImages = new WeakMap();
    this.activeImage = null;
    this.boundSwiper = null;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('mousemove', this.#handleMouseMove);
    this.#bindSwiperReset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('mousemove', this.#handleMouseMove);
    this.#unbindSwiperReset();
    this.#resetAll();
  }

  /**
   * @param {Event} event
   * @returns {HTMLImageElement | null}
   */
  #getZoomable(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const el = target.closest(ZOOMABLE_SELECTOR);
    if (!el || !this.contains(el) || el.tagName !== 'IMG') return null;
    return el;
  }

  /**
   * @param {HTMLImageElement} img
   */
  #ensureStyles(img) {
    if (img.dataset.zoomReady === 'true') return;
    img.style.transition = `transform ${this.transitionDuration} ease-out`;
    img.dataset.zoomReady = 'true';
  }

  /**
   * @param {HTMLImageElement} img
   * @param {MouseEvent} event
   */
  #zoomIn(img, event) {
    this.#resetAll();
    this.#ensureStyles(img);

    const rect = img.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    img.style.transformOrigin = `${x}% ${y}%`;
    img.style.transform = `scale(${this.zoomScale})`;
    img.classList.add('is-zoomed');
    this.zoomedImages.set(img, true);
    this.activeImage = img;
    this.#setSwiperTouch(false);
  }

  /**
   * @param {HTMLImageElement} img
   */
  #zoomOut(img) {
    this.#ensureStyles(img);
    img.style.transform = 'scale(1)';
    img.style.transformOrigin = 'center center';
    img.classList.remove('is-zoomed');
    this.zoomedImages.set(img, false);
    if (this.activeImage === img) this.activeImage = null;
    this.#setSwiperTouch(true);
  }

  #resetAll = () => {
    if (this.activeImage) {
      this.#zoomOut(this.activeImage);
    }

    this.querySelectorAll(`${ZOOMABLE_SELECTOR}.is-zoomed`).forEach((img) => {
      if (img instanceof HTMLImageElement) this.#zoomOut(img);
    });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const img = this.#getZoomable(event);

    if (!img) {
      if (
        event.target instanceof Element &&
        event.target.closest(
          '.product-media-desktop-thumb, .product-media-mobile-thumb, .product-media-nav-btn'
        )
      ) {
        this.#resetAll();
      }
      return;
    }

    event.preventDefault();

    if (this.zoomedImages.get(img)) {
      this.#zoomOut(img);
    } else {
      this.#zoomIn(img, event);
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    const img = this.activeImage;
    if (!img || !this.zoomedImages.get(img)) return;
    if (this.#getZoomable(event) !== img) return;

    const rect = img.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = `${x}% ${y}%`;
  };

  #bindSwiperReset() {
    let attempts = 0;
    const tryBind = () => {
      const swiper = this.querySelector('.product-media__wrapper')?.swiper;
      if (swiper) {
        swiper.on('slideChange', this.#resetAll);
        this.boundSwiper = swiper;
        return;
      }
      if (attempts < 40) {
        attempts += 1;
        requestAnimationFrame(tryBind);
      }
    };
    tryBind();
  }

  #unbindSwiperReset() {
    this.boundSwiper?.off?.('slideChange', this.#resetAll);
    this.boundSwiper = null;
  }

  /**
   * @param {boolean} allow
   */
  #setSwiperTouch(allow) {
    const swiper =
      this.boundSwiper || this.querySelector('.product-media__wrapper')?.swiper;
    if (swiper) swiper.allowTouchMove = allow;
  }
}

customElements.define('product-media-zoom', ProductMediaZoom);
